import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { adminEndpoints } from "@/lib/adminApi";
import Icon from "@/components/Icon";

const StatCard = ({ title, value, icon, tint = "cyan", testId }) => (
  <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div>
        <div className="text-slate-500 text-sm font-medium">{title}</div>
        <div className="font-heading text-3xl font-bold text-[#0A1628] mt-2 tabular-nums" data-testid={testId ? `${testId}-value` : undefined}>
          {value?.toLocaleString?.("en-US") ?? value}
        </div>
      </div>
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          tint === "cyan" ? "bg-cyan-50 text-[#00b8cc]" : "bg-slate-100 text-[#0A1628]"
        }`}
      >
        <Icon name={icon} size={22} />
      </div>
    </div>
  </div>
);

const STATUS_LABEL = {
  new: "جديد",
  contacted: "تم التواصل",
  completed: "مكتمل",
  cancelled: "ملغى",
};

const STATUS_COLOR = {
  new: "bg-cyan-100 text-cyan-800",
  contacted: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-slate-200 text-slate-700",
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    adminEndpoints.dashboard().then(setData).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-3xl font-bold text-[#0A1628]">لوحة المعلومات</h1>
      <p className="text-slate-500 mt-1">نظرة سريعة على نشاط الموقع.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <StatCard title="طلبات جديدة" value={data?.new_quotes ?? 0} icon="send" testId="stat-new-quotes" />
        <StatCard title="إجمالي الطلبات" value={data?.total_quotes ?? 0} icon="boxes" tint="slate" testId="stat-total-quotes" />
        <StatCard title="المنتجات" value={data?.total_products ?? 0} icon="boxes" tint="slate" testId="stat-total-products" />
        <StatCard title="المشاريع" value={data?.total_projects ?? 0} icon="sparkles" tint="slate" testId="stat-total-projects" />
      </div>

      <div className="mt-10 bg-white border border-slate-100 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-heading font-bold text-[#0A1628]">آخر 5 طلبات</h2>
          <Link to="/admin/quote-requests" className="text-sm text-[#00b8cc] hover:underline">
            عرض الكل
          </Link>
        </div>
        <div className="divide-y divide-slate-100" data-testid="dashboard-latest-quotes">
          {(data?.latest_quotes ?? []).map((q) => (
            <div key={q.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-[#0A1628]">{q.name}</div>
                <div className="text-slate-500 text-sm" dir="ltr">
                  {q.phone}
                  {q.product_name && <span className="ms-3">— {q.product_name}</span>}
                </div>
              </div>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${STATUS_COLOR[q.status] || "bg-slate-100 text-slate-700"}`}>
                {STATUS_LABEL[q.status] || q.status}
              </span>
              <div className="text-xs text-slate-400 tabular-nums" dir="ltr">
                {new Date(q.created_at).toLocaleDateString("en-GB")}
              </div>
            </div>
          ))}
          {data && data.latest_quotes.length === 0 && (
            <div className="px-6 py-8 text-center text-slate-500">لا توجد طلبات بعد.</div>
          )}
        </div>
      </div>
    </div>
  );
}
